import React, { useState, useEffect } from 'react';

interface BaseCreditTermsFormData {
  base_credit_terms: string;
  base_credit_terms_desc: string;
}

interface BaseCreditTermsModalProps {
  isOpen: boolean;
  organization: {
    base_credit_terms?: string | null;
    base_credit_terms_desc?: string | null;
  };
  onClose: () => void;
  onSubmit: (formData: BaseCreditTermsFormData) => Promise<void>;
}

const BaseCreditTermsModal: React.FC<BaseCreditTermsModalProps> = ({
  isOpen,
  organization,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState<BaseCreditTermsFormData>({
    base_credit_terms: '',
    base_credit_terms_desc: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Initialize form data when organization changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        base_credit_terms: organization.base_credit_terms || '',
        base_credit_terms_desc: organization.base_credit_terms_desc || ''
      });
      setError('');
    }
  }, [isOpen, organization]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update base credit terms');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof BaseCreditTermsFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-6">
            Edit Base Credit Terms
          </h3>
          
          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Credit Terms
                </label>
                <input
                  type="text"
                  value={formData.base_credit_terms}
                  onChange={(e) => handleInputChange('base_credit_terms', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Net 30, Net 60, etc."
                  maxLength={100}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.base_credit_terms_desc}
                  onChange={(e) => handleInputChange('base_credit_terms_desc', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter a description for the base credit terms..."
                  rows={4}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-8">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BaseCreditTermsModal;

